import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { setConfiguration } from 'react-grid-system';

import {
  AccordionSection,
  AnnouncementSection,
  HeroSection,
  MediaTextSection,
  FeatureListSection,
  MediaGridSection,
  SectionContained,
  SidebarSection,
  TextSection,
  MarketoFormSection,
  TypeformSection,
  VideoSection,
} from '../components/Sections';
import { getObjectProperty } from '../utils';
import InfoPane from '../components/InfoPane/index';
import Layout from '.';
import SEO from '../components/Seo';
import Quote from '../components/Quote/index';

import '../sass/index.scss';

setConfiguration({ gutterWidth: 32 });

const handleAccordion = section => (<AccordionSection key={section.id} {...section} />);

const handleAnnouncement = section => (<AnnouncementSection key={section.id} {...section} />);

const handleBlockquote = section => (<Quote key={section.id} {...section} />);

const handleFeatureList = section => (<FeatureListSection key={section.id} {...section} />);

const handleInfoPane = section => (<InfoPane key={section.id} items={section.items} />);

const handleMarketoFormSection = section => (<MarketoFormSection key={section.id} {...section} />);

const handleMediaGridSection = section => (<MediaGridSection key={section.id} {...section} />);

const handleMediaTextSection = section => (<MediaTextSection key={section.id} {...section} />);

const handleSectionContained = section => (<SectionContained key={section.id} {...section} />);

const handleSidebarSection = section => (<SidebarSection key={section.id} {...section} />);

const handleTextSection = section => (<TextSection key={section.id} {...section} />);

const handleTypeformSection = section => (<TypeformSection key={section.id} {...section} />);

const handleVideo = section => (<VideoSection key={section.id} {...section} />);

const LandingPage = (props) => {
  const data = props.data.contentfulLandingPage;

  const sections = {
    ContentfulAccordion: handleAccordion,
    ContentfulAnnouncement: handleAnnouncement,
    ContentfulBlockquote: handleBlockquote,
    ContentfulFeatureList: handleFeatureList,
    ContentfulInfoPane: handleInfoPane,
    ContentfulMarketoForm: handleMarketoFormSection,
    ContentfulMediaGridSection: handleMediaGridSection,
    ContentfulMediaTextSection: handleMediaTextSection,
    ContentfulSectionContained: handleSectionContained,
    ContentfulSidebarSection: handleSidebarSection,
    ContentfulTextSection: handleTextSection,
    ContentfulTypeform: handleTypeformSection,
    ContentfulVideo: handleVideo,
  };

  let hero = null;

  if (data.heroSection) {
    hero = (
      <HeroSection {...data.heroSection} />
    );
  }

  const seoSettings = {
    description: data.seo && data.seo.description,
    keywords: (data.seo && data.seo.keywords) || [],
    image: data.seo && data.seo.featuredImage ? `https:${data.seo.featuredImage.file.url}` : '',
    title: (data.seo && data.seo.title) || data.title,
    overwriteTitle: data.seo && data.seo.title,
  };

  return (
    <Layout>
      <SEO {...seoSettings} />

      {hero}

      {data.sections && data.sections.map((section) => {
        const sectionHandler = getObjectProperty(sections, section.__typename, null);

        if (sectionHandler) {
          return sectionHandler(section);
        }

        return null;
      })}
    </Layout>
  );
};

export default LandingPage;

LandingPage.propTypes = {
  data: PropTypes.object,
};

export const landingPageQuery = graphql`
  query($slug: String!) {
    contentfulLandingPage(slug: {eq: $slug}) {
      id,
      title,
      heroSection {
        ...Hero
      },
      sections {
        __typename,
        ... on Node {
          ... on ContentfulAccordion {
            ...Accordion
          }

          ... on ContentfulAnnouncement {
            ...Announcement
          }

          ... on ContentfulBlockquote {
            ...Blockquote
          }

          ... on ContentfulFeatureList {
            ...FeatureList
          }

          ... on ContentfulInfoPane {
            ...InfoPane
          }

          ... on ContentfulMarketoForm {
            ...MarketoForm
          }

          ... on ContentfulMediaGridSection {
            ...MediaGridSection
          }

          ... on ContentfulMediaTextSection {
            ...MediaTextSection
          }
            
          ...on ContentfulSectionContained {
            ...SectionContained
          }

          ... on ContentfulSidebarSection {
            ...SidebarSection
          }

          ... on ContentfulTextSection {
            ...TextSection
          }

          ... on ContentfulTypeform {
            ...Typeform
          }

          ...on ContentfulVideo {
            ...Video
          }
        }
      },
      seo {
        ...Seo
      }
    }
  }
`;
